import React, { useEffect, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Avatar, { AvatarItem } from '@atlaskit/avatar';
import AvatarGroup from '@atlaskit/avatar-group';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { Approver } from '@atlassian/jira-workflow-approvals/src/common/types.tsx';
import messages from './messages.tsx';
import type { SUPPORTED_WHO_CHOOSES_APPROVERS } from './types.tsx';

export type Props = {
	approvalStatus: string;
	whoChoosesApprovers: SUPPORTED_WHO_CHOOSES_APPROVERS;
	numberOfApprovers: number;
	approverListFieldName: string;
	defaultApprovers: AccountId[];
	cachedApprovers: Approver[];
	onFetchApprovers?: (accountIds: AccountId[]) => void;
	tooltipDelay?: number;
	testId?: string;
};

const getApprovalInfoMessage = (length: number): MessageDescriptor => {
	if (length > 1) {
		return messages.approvalStepInformationWithDefaults;
	}
	if (length === 1) {
		return messages.approvalStepInformationWithDefaultsSingular;
	}
	return messages.approvalStepInformationNoDefaults;
};

export const ApprovalStepView = (props: Props) => {
	const {
		defaultApprovers,
		approvalStatus,
		whoChoosesApprovers,
		numberOfApprovers,
		approverListFieldName,
		cachedApprovers,
		onFetchApprovers,
		tooltipDelay,
		testId,
	} = props;
	const { formatMessage } = useIntl();

	useEffect(() => {
		if (onFetchApprovers !== undefined && defaultApprovers.length > 0) {
			onFetchApprovers(defaultApprovers);
		}
	}, [onFetchApprovers, defaultApprovers]);

	const enrichedDefaultApprovers: Approver[] = defaultApprovers
		.map((approver) => cachedApprovers.find((ca) => ca.accountId === approver))
		.filter(Boolean);

	const approvalStepInformationMessage = getApprovalInfoMessage(defaultApprovers.length);

	return (
		<ApprovalStepCard>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p>{formatMessage(messages.approvalStepHeading, { approvalStatus })}</p>
			<ApprovalStepInformation>
				{formatMessage(approvalStepInformationMessage, {
					whoChoosesApprovers,
					numberOfApprovers,
					approverListFieldName,
					approvalStatus,
				})}
			</ApprovalStepInformation>
			{enrichedDefaultApprovers.length === 1 && (
				<AvatarItem
					avatar={
						<Tooltip
							content={enrichedDefaultApprovers[0].name || ''}
							position="bottom"
							delay={tooltipDelay}
						>
							<Avatar src={enrichedDefaultApprovers[0].avatarUrl} testId={testId} />
						</Tooltip>
					}
					key={enrichedDefaultApprovers[0].accountId}
					primaryText={enrichedDefaultApprovers[0].name}
				/>
			)}
			{enrichedDefaultApprovers.length > 1 && (
				<AvatarGroup
					appearance="stack"
					data={enrichedDefaultApprovers.map((approver) => ({
						name: approver.name || '',
						src: approver.avatarUrl,
						appearance: 'circle',
						size: 'medium',
						enableTooltip: true,
					}))}
				/>
			)}
		</ApprovalStepCard>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ApprovalStepCard = styled.div<{
	children?: ReactNode;
}>({
	boxSizing: 'border-box',
	borderWidth: '2px',
	borderRadius: '3px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.inverse.subtle'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	boxShadow: `0 1px 1px ${token('color.background.input')}, 0 0 1px ${token('color.background.input')}`,
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	marginTop: token('space.100'),
	marginRight: 0,
	marginBottom: token('space.100'),
	marginLeft: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ApprovalStepInformation = styled.p<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text'),
});
